type Amount = string | number

type OpenLongAmountParams = {
  price?: Amount
  markPrice?: Amount
  leverage?: number
  balance?: Amount
}

type TrustMarginParams = {
  qty?: Amount
  price?: Amount
  markPrice?: Amount
  leverage?: string
  direction?: string
  contractValue?: string
}

type InitMarginParams = {
  marginPrice?: Amount
  contractValue?: string
  leverage?: string
  qty?: Amount
}

type ContractValueParams = {
  qty?: Amount
  price?: Amount
  contractValue?: string
}

type GetPnlParams = {
  openPrice?: Amount
  closePrice?: Amount
  qty?: Amount
  contractValue?: string
  dir?: string
}

/**
 *  String? margin,
    String? holePrice,
    String? symbolID,
    String? qty,
    String? contractValue,
    String? side,
    String? pricePrecision, //价格精度
    String takerFee = '0',
 */
type GetLiquidatePriceOfPositionParams = {
  margin?: Amount
  holePrice?: Amount
  symbolId?: SymbolId
  qty?: Amount
  contractValue?: Amount
  side?: number
  pricePrecision?: number
  takerFee?: Amount
}

export class ContractCalculation {
  /**
   * 获取开多数量
   * @param {string | number} params.price 买入价格
   * @param {string | number} params.markPrice 标记价格
   * @param {number} params.leverage 杠杆倍数
   * @param {string | number} params.balance 可用余额
   * @returns 开多数量
   */
  static getOpenLongAmount = (params: OpenLongAmountParams) => {
    const { price = '0', markPrice = '0', leverage, balance } = params
    let openLoss = '0'
    if (price.isGreaterThan(markPrice)) {
      openLoss = price.subtractStr(markPrice)
    }
    const temp = price.divideStr(leverage).addStr(openLoss)
    const result = balance?.divideStr(temp)
    return result || '0'
  }

  /**
   * 获取开空数量
   * @param {string | number} params.price 买入价格
   * @param {string | number} params.markPrice 标记价格
   * @param {number} params.leverage 杠杆倍数
   * @param {string | number} params.balance 可用余额
   * @returns 开多数量
   */
  static getOpenShortAmount = (params: OpenLongAmountParams) => {
    const { price = '0', markPrice = '0', leverage, balance } = params
    let openLoss = '0'
    if (price.isLessThan(markPrice)) {
      openLoss = markPrice.subtractStr(price)
    }
    const temp = price.divideStr(leverage).addStr(openLoss)
    const result = balance?.divideStr(temp)
    return result || '0'
  }

  static contractValue(params: ContractValueParams) {
    const { price = '0', qty = '0', contractValue = '0' } = params
    return qty.multiplyStr(contractValue).multiplyStr(price)
  }

  static initMargin = (params: InitMarginParams) => {
    const value = ContractCalculation.contractValue({
      price: params.marginPrice,
      qty: params.qty,
      contractValue: params.contractValue,
    })

    return value.divideStr(params.leverage)
  }

  static openLoss = (params: TrustMarginParams) => {
    const { price = '0', markPrice = '0', qty, contractValue = '0', direction } = params
    const quantity = qty?.multiplyStr(contractValue)
    let temIndex = markPrice.subtractStr(price).multiplyStr(direction!)
    if ((temIndex || 0).isGreaterThanOrEqualTo(0)) {
      return '0'
    } else {
      temIndex = temIndex.multiplyStr(-1)
    }
    const openLoss = quantity?.multiplyStr(temIndex)
    return openLoss
  }

  static trustMargin = (params: TrustMarginParams) => {
    const initMargin = ContractCalculation.initMargin({
      marginPrice: params.price,
      leverage: params.leverage,
      qty: params.qty,
      contractValue: params.contractValue,
    })
    const openLoss = ContractCalculation.openLoss(params)
    const trustMarginValue = initMargin.addStr(openLoss)
    return trustMarginValue.ceil(2).decimalPadZero(2)
  }

  static getPnl = (params: GetPnlParams) => {
    const { openPrice = '0', closePrice = '0', qty = '0', contractValue = '0', dir = '0' } = params
    const pnl = closePrice.subtractStr(openPrice).multiplyStr(qty).multiplyStr(contractValue).multiplyStr(dir)
    return pnl
  }

  /**
   * 获取强平价
   * (仓位保证金 + 保证金速算额  - 合约方向 * 持仓量 * 平均持仓价格 )  /  (持仓量 * 维持保证金率 - 合约方向 * 持仓量) / (1+合约方向*手续费率)
   * @param params.margin 仓位保证金
   * @param params.holePrice 平均持仓价格
   * @param params.symbolId 合约ID
   * @param params.qty 持仓量
   * @param params.contractValue
   * @param params.side 合约方向
   * @param params.pricePrecision 价格精度
   * @param params.takerFee 手续费率
   * @returns
   */
  static getLiquidatePriceOfPosition = (params: GetLiquidatePriceOfPositionParams, symbolConfig: ApiResponse.ContractExchangeInfoSymbol | undefined) => {
    const { margin = '0', holePrice = '0', qty = '0', contractValue = '0', side = '0', pricePrecision = 2, takerFee = '0' } = params
    const netFeeRate = takerFee.multiplyStr(side).multiplyStr(-1)
    const pVal = qty.multiplyStr(contractValue) // 持仓量
    const positionValue = pVal.multiplyStr(holePrice) // 持仓价值

    const levels = symbolConfig?.maintMarginRate

    const level = levels?.find((item) => {
      return item.minAmount.isLessThanOrEqualTo(positionValue) && item.maxAmount.isGreaterThan(positionValue)
    })

    const maintainMarginRate = level?.marginRate || 0
    const maintAmount = level?.maintAmount || 0

    // 分子 = 仓位保证金 + 保证金速算额  - 合约方向 * 持仓量 * 平均持仓价格
    const numerator = margin.addStr(maintAmount).subtractStr(side.multiplyStr(pVal).multiplyStr(holePrice))

    // 分母 = (持仓量 * 维持保证金率 - 合约方向 * 持仓量)
    const denominator = pVal.multiplyStr(maintainMarginRate).subtractStr(side.multiplyStr(pVal))

    const liquidatePrice = numerator.divideStr(denominator).divideStr(netFeeRate.addStr('1'))

    if (side === 1) {
      return liquidatePrice.ceil(pricePrecision || 2)
    }
    return liquidatePrice.cutDecimal(pricePrecision || 2)
  }
}
